import React, { useState, useEffect } from 'react'
import { useStaticQuery, graphql, Link, navigate } from 'gatsby'
import tw, { css, styled } from 'twin.macro'

import { Global } from '@emotion/react'
import { ChevronDown, ChevronUp } from 'react-feather'
import { Menu, MenuList, MenuButton, MenuItem } from '@reach/menu-button'
/** Hooks */
import useCart from '../../../hooks/useCart'
/** Page components */
import Layout from '../../../components/layout'
import Meta from '../../../components/meta'
import LineItems from '../../../components/line-items'

const isBrowser = typeof window !== 'undefined'

/** Styles */
const GlobalStyles = () => (
	<Global
		styles={css`
			.quantity-button[data-reach-menu-button] {
				${tw`inline-flex items-center`}
				${tw`border border-gray-600 rounded bg-gray-100 text-gray-600 text-xs py-1 px-1 leading-none`}
				outline: 0;
				&:hover,
				&:focus {
					outline: 0;
					box-shadow: 0 0 0 3px rgba(88, 200, 223, 0.85);
				}
				span {
					${tw`pl-2 pr-1 font-semibold`}
				}
			}
			.quantity-options[data-reach-menu-list] {
				${tw`border border-gray-600 rounded bg-gray-100 text-gray-600 text-sm py-2 px-1 my-2 shadow-lg`}
				outline: 0;
				&:hover,
				&:focus {
					outline: 0;
					box-shadow: 0 0 0 3px rgba(88, 200, 223, 0.85);
				}
				[data-reach-menu-item] {
					${tw`rounded-sm py-px px-0 hover:bg-gray-50 cursor-pointer`}
					outline: 0;
					&:hover,
					&:focus {
						outline: 0;
						box-shadow: 0 0 0 3px rgba(88, 200, 223, 0.85);
					}
				}
			}
		`}
	/>
)
const Align = styled.div`
	${tw`flex flex-col md:flex-row`}
`
const Wrapper = tw.div`bg-gray-100 py-4 md:py-8 px-2 md:px-4`
const ItemsContainer = tw.section`max-w-7xl mx-auto sm:px-6 lg:px-8`
const ItemsWrapper = styled.div`
	${tw`w-full md:w-2/3`}
	${tw`bg-white shadow overflow-hidden sm:rounded-md`}
`
const SummeryWrapper = styled(ItemsWrapper)`
	${tw`w-full md:w-1/3`}
	${tw`flex flex-col ml-0 mt-4 md:ml-4 md:mt-0`}
`
const ItemWrapper = styled.div`
	${tw`block hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition duration-150 ease-in-out`}
`
const Item = tw.div`flex flex-col items-center px-4 py-4 sm:px-6`
const Summery = styled(Item)`
	${tw``}
`
const CheckoutButton = styled.button`
	${tw`bg-yellow-500 text-gray-700 px-4 py-2 text-lg w-full rounded text-center`}
	&[disabled] {
		${tw`bg-yellow-200 text-gray-400 cursor-not-allowed`}
	}
`
const Content = styled.div`
	${tw`min-w-0 w-full flex-1 flex items-center`}
	.product-details {
		${tw`min-w-0 flex-1 px-4 grid grid-cols-2 gap-4`}
	}
	.product-selectors {
		${tw`block ml-auto`}
	}
`
const Price = tw.div`font-semibold text-gray-600`
const Heading1 = tw.h1`text-gray-800 text-4xl font-semibold font-display mb-2 md:mb-4 pt-4 md:pt-8`
const EmptyCart = tw.div`flex flex-col justify-start items-center w-full my-4 text-gray-600`
const EmptyMessage = tw.div`flex justify-center mb-2`
const EmptyButton = styled(Link)`
	${tw`flex bg-yellow-500 hover:bg-yellow-400 font-display text-black text-base px-4 py-1 rounded`}
`

export default function CartPage(props) {
	const {
		count,
		items,
		updateQuantity,
		incrementInCart,
		decrementInCart,
		removeFromCart,
		total,
	} = useCart()
	const [isInput, setIsInput] = useState(items.map(() => false))
	const showInput = i => {
		const x = isInput.map((x, index) => (index === i ? true : false))
		return setIsInput(x)
	}
	useEffect(() => {
		/**
		 * Google Analytics - Tag Manager - Enhanced Ecommerce
		 * Measuring checkout steps - 1. Cart page
		 * */
		let isFresh = true
		if (isBrowser && window.dataLayer && total > 1) {
			const products = items.map(
				({ name, price, sku, quantity, brand, category }) => ({
					name,
					id: sku,
					price: Number((price / 100).toFixed(2)),
					quantity,
					brand,
					category,
				})
			)
			window.dataLayer.push({
				event: 'checkout1',
				ecommerce: {
					checkout: {
						actionField: { step: 1 },
						products,
					},
				},
			})
		}
		return () => (isFresh = false)
	})

	const [render, setRender] = useState(0)

	useEffect(() => {
		setRender(prev => prev + 1)
	}, [])

	return (
		<Layout key={render}>
			<Meta
				title={'Shopping Cart'}
				keywords={['shopping cart']}
				description={'Your Maytronics shopping cart'}
			/>
			<GlobalStyles />
			<Wrapper>
				<ItemsContainer>
					<Heading1>Shopping Cart</Heading1>
					<Align>
						<ItemsWrapper>
							{total > 1 ? (
								<ul>
									{items.map((item, index) => {
										const { img, name, slug, price, sku, quantity } = item
										return (
											<li key={name}>
												<ItemWrapper>
													<Item>
														<Content>
															<div css={tw`flex-shrink-0`}>
																<img
																	css={tw`w-24 h-24 rounded-md bg-gray-200`}
																	src={`${
																		img.split('?')[0]
																	}?fm=png&fl=png8&fit=pad&w=256&h=256`}
																	alt={name}
																/>
															</div>
															<div className="product-details">
																<div>
																	<div
																		css={tw`text-base leading-5 font-medium text-gray-800 truncate`}
																	>
																		{name}
																	</div>
																	<div
																		css={tw`mt-2 flex items-center text-sm leading-5 text-gray-500`}
																	>
																		<span css={tw`truncate`}>{sku}</span>
																	</div>
																	<Price
																		css={tw`mt-2 flex items-center text-sm leading-5 text-gray-500`}
																	>
																		{Number(price / 100).toLocaleString(
																			'en-AU',
																			{
																				style: 'currency',
																				currency: 'AUD',
																			}
																		)}
																	</Price>
																</div>
																<div className="product-selectors">
																	<div css={tw`flex flex-col justify-end`}>
																		<Price
																			css={tw`text-base leading-none text-gray-600`}
																		>
																			{Number(
																				(price / 100) * quantity
																			).toLocaleString('en-AU', {
																				style: 'currency',
																				currency: 'AUD',
																			})}
																		</Price>
																		<div
																			css={tw`text-xs leading-none text-gray-600 mt-4 ml-auto`}
																		>
																			{isInput[index] ? (
																				<div css={tw`inline-flex items-center`}>
																					<input
																						type="text"
																						value={quantity}
																						onChange={event =>
																							event.target.value >= 1 &&
																							updateQuantity(
																								index,
																								event.target.value
																							)
																						}
																						css={tw`w-8 border border-gray-600 rounded bg-gray-100 py-1 px-1 appearance-none`}
																					/>
																					<div css={tw`flex flex-col`}>
																						<button
																							type="button"
																							onClick={() =>
																								incrementInCart(index)
																							}
																							css={tw`flex justify-center items-center w-4 h-3 border border-gray-600 rounded bg-gray-100 ml-1 mb-px appearance-none`}
																						>
																							<ChevronUp
																								size={10}
																								css={{ strokeWidth: '3px' }}
																								aria-hidden
																							/>
																						</button>
																						<button
																							type="button"
																							onClick={() =>
																								quantity >= 2 &&
																								decrementInCart(index)
																							}
																							css={tw`flex justify-center items-center w-4 h-3 border border-gray-600 rounded bg-gray-100 ml-1 appearance-none`}
																						>
																							<ChevronDown
																								size={10}
																								css={{ strokeWidth: '3px' }}
																								aria-hidden
																							/>
																						</button>
																					</div>
																				</div>
																			) : (
																				<Menu>
																					<MenuButton className="quantity-button">
																						Qty:
																						<span aria-hidden>{quantity}</span>
																						<ChevronDown
																							size={16}
																							css={{ strokeWidth: '2px' }}
																							aria-hidden
																						/>
																					</MenuButton>
																					<MenuList className="quantity-options">
																						<MenuItem
																							onSelect={() =>
																								removeFromCart(item)
																							}
																						>
																							0 (Remove)
																						</MenuItem>
																						<MenuItem
																							onSelect={() =>
																								updateQuantity(index, 1)
																							}
																						>
																							1
																						</MenuItem>
																						<MenuItem
																							onSelect={() =>
																								updateQuantity(index, 2)
																							}
																						>
																							2
																						</MenuItem>
																						<MenuItem
																							onSelect={() =>
																								updateQuantity(index, 3)
																							}
																						>
																							3
																						</MenuItem>
																						<MenuItem
																							onSelect={() =>
																								updateQuantity(index, 4)
																							}
																						>
																							4
																						</MenuItem>
																						<MenuItem
																							onSelect={() =>
																								updateQuantity(index, 5)
																							}
																						>
																							5
																						</MenuItem>
																						<MenuItem
																							onSelect={() =>
																								updateQuantity(index, 6)
																							}
																						>
																							6
																						</MenuItem>
																						<MenuItem
																							onSelect={() =>
																								updateQuantity(index, 7)
																							}
																						>
																							7
																						</MenuItem>
																						<MenuItem
																							onSelect={() =>
																								updateQuantity(index, 8)
																							}
																						>
																							8
																						</MenuItem>
																						<MenuItem
																							onSelect={() =>
																								updateQuantity(index, 9)
																							}
																						>
																							9
																						</MenuItem>
																						<hr
																							css={tw`border-0 border-t border-gray-600`}
																						/>
																						<MenuItem
																							onSelect={() => showInput(index)}
																						>
																							10+
																						</MenuItem>
																					</MenuList>
																				</Menu>
																			)}
																		</div>
																		<div
																			css={tw`mt-2 flex items-center text-sm leading-5 text-gray-500 ml-auto`}
																		>
																			<button
																				css={tw`flex text-xs text-blue-600`}
																				onClick={() => removeFromCart(item)}
																				onKeyDown={() => removeFromCart(item)}
																			>
																				Remove
																			</button>
																		</div>
																	</div>
																</div>
															</div>
														</Content>
													</Item>
												</ItemWrapper>
											</li>
										)
									})}
								</ul>
							) : (
								<EmptyCart>
									<EmptyMessage>
										<span role="img" aria-labelledBy="cobweb">
											🕸
										</span>
										<span>
											&nbsp; Things are looking a little empty in here &nbsp;
										</span>
										<span role="img" aria-labelledBy="package">
											📦
										</span>
									</EmptyMessage>
									<EmptyButton to={`/shop`}>Add Products</EmptyButton>
								</EmptyCart>
							)}
						</ItemsWrapper>
						<SummeryWrapper>
							<Summery>
								<CheckoutButton
									onClick={() => navigate(`/shop/checkout`)}
									onKeyDown={() => navigate(`/shop/checkout`)}
									disabled={total === 0}
								>
									Go to checkout
								</CheckoutButton>
								<div
									css={tw`flex items-center justify-between px-4 py-4 w-full`}
								>
									<span css={tw`inline align-middle`}>
										Items
										<span css={tw`inline-block text-sm pl-1`}>({count})</span>
									</span>
									<span>
										{Number(total / 100).toLocaleString('en-AU', {
											style: 'currency',
											currency: 'AUD',
										})}
									</span>
								</div>
								<hr css={tw`border-0 border-t border-gray-300 w-full my-1`} />
								<div
									css={tw`flex items-center justify-between px-4 py-4 w-full`}
								>
									<span css={tw`text-xl`}>Total</span>
									<span css={tw`text-xl`}>
										{Number(total / 100).toLocaleString('en-AU', {
											style: 'currency',
											currency: 'AUD',
										})}
									</span>
								</div>
							</Summery>
						</SummeryWrapper>
					</Align>
				</ItemsContainer>
			</Wrapper>
		</Layout>
	)
}
